import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Col, Progress, Row, Tooltip } from 'antd';
import { Link } from 'react-admin';

import { CallsReportNumbers, reportsHttp } from '@network';
import { InitialCard } from '@components';
import { querySerialize } from '@utils';
import { useWindowSize } from '@hooks';

interface Props {
  range: string;
  setLoader: (num: number, value: boolean) => void;
}

export const CallsReport = ({ range, setLoader }: Props) => {
  const [numbers, setNumbers] = useState<CallsReportNumbers>();
  const { width } = useWindowSize();
  const cls = useStyles();

  const loadNumbers = async () => {
    setLoader(11, true);
    try {
      const nextNumbers = await reportsHttp.callsReport({ range });
      setNumbers(nextNumbers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(11, false);
    }
  };

  useEffect(() => void loadNumbers(), [range]);

  if (!numbers) {
    return null;
  }

  const overdue = numbers.overdue;
  const future = numbers.future;

  const overdueColor = overdue >= 41 ? 'red' : (overdue >= 11 ? 'yellow' : '#8FD14F');
  const futureColor = future >= 20 ? 'red' : (future >= 15 ? 'yellow' : '#8FD14F');

  const overdueLink = '/credit-call?' + querySerialize({ filter: JSON.stringify({ callDate: 'overdue' }) });
  const futureLink = '/credit-call?' + querySerialize({ filter: JSON.stringify({ callDate: 'future' }) });

  return (
    <InitialCard
      center="Overdue Calls"
      extraPadding
      fullHeight
      boxClass={cls.box}
      contentClass={cls.content}
      left={<div/>}
      right={
        <Tooltip title="Not affected by time filter" placement="left" color="#042E6B">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="#F38336">
            <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 27-3 53t-10 51q-14-16-32.5-27T794-418q3-15 4.5-30.5T800-480q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93q51 0 97.5-15t85.5-42q12 17 29.5 30t37.5 20q-51 41-114.5 64T480-80Zm290-160q-21 0-35.5-14.5T720-290q0-21 14.5-35.5T770-340q21 0 35.5 14.5T820-290q0 21-14.5 35.5T770-240Zm-158-52L440-464v-216h80v184l148 148-56 56Z"/>
          </svg>
        </Tooltip>
      }
    >
      <Row gutter={[20, 20]}>
        <Col sm={24} md={12} className={cls.col}>
          <Link to={overdueLink}>
            <div className={cls.title}>Overdue Calls</div>
            <Progress
              type="circle"
              width={width && width >= 768 && width < 1400 ? 120 : 190}
              percent={100}
              style={{ marginTop: 8 }}
              strokeColor={overdueColor}
              trailColor={'black'}
              className={cls.progress}
              format={() => <span className={cls.counter}>{overdue}</span>}
            />
          </Link>
        </Col>
        <Col sm={24} md={12} className={cls.col}>
          <Link to={futureLink}>
            <div className={cls.title}>Future Calls</div>
            <Progress
              type="circle"
              width={width && width >= 768 && width < 1400 ? 120 : 190}
              percent={100}
              style={{ marginTop: 8 }}
              strokeColor={futureColor}
              trailColor={'black'}
              className={cls.progress}
              format={() => <span className={cls.counter}>{future >= 20 ? '20+' : future}</span>}
            />
          </Link>
        </Col>
      </Row>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  box: {
    paddingBottom: '10px !important',
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btn: {
    padding: 0,
    textTransform: 'none',
    display: 'block',
    width: '100%',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    fontSize: 16,
    color: 'var(--title-color)',
  },
  content: {
    paddingTop: 20,
    paddingBottom: 17,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  counter: {
    marginTop: 5,
    fontSize: 50,
    fontWeight: 700,
    color: 'var(--title-color)',
  },
});
